import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Edit3, Pause, Play, Plus, RefreshCw, Server, Trash2, Wifi } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useVms, useVmActions } from '@/hooks/useDispatcher';
import { useAuthStore } from '@/stores/authStore';
import { formatRelative, maskSecret } from '@/lib/utils';
import { VmFormDialog } from './settings/VmFormDialog';
import type { Vm } from '@/types/api';

export default function VmsPage() {
  const vms = useVms();
  const { pause, resume, remove, probe } = useVmActions();
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Vm | undefined>(undefined);

  function handleRemove(vm: Vm) {
    if (!confirm(`Supprimer définitivement la VM "${vm.slug}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    remove.mutate(vm.id, {
      onSuccess: () => toast.success(`VM ${vm.slug} supprimée`),
      onError: () => toast.error('Échec de la suppression'),
    });
  }

  function handleTest(vm: Vm) {
    const id = toast.loading(`Test ${vm.slug}…`);
    probe.mutate(vm.id, {
      onSuccess: (res) => {
        toast.dismiss(id);
        if (res.ok) toast.success(`${vm.slug} : connexion OK`);
        else toast.error(`${vm.slug} : ${res.details ?? 'KO'}`);
      },
      onError: () => {
        toast.dismiss(id);
        toast.error('Test impossible');
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="VMs"
        description="Pool LeadBridge"
        action={
          isAdmin && (
            <Button onClick={() => setShowCreate(true)}>
              <Plus size={14} /> Ajouter une VM
            </Button>
          )
        }
      />

      {vms.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : !vms.data?.length ? (
        <Card>
          <EmptyState
            icon={Server}
            title="Aucune VM enregistrée"
            description="Ajoutez une VM LeadBridge pour commencer à dispatcher des jobs."
            action={
              isAdmin && (
                <Button onClick={() => setShowCreate(true)}>
                  <Plus size={14} /> Ajouter une VM
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {vms.data.map((vm) => (
            <VmCard
              key={vm.id}
              vm={vm}
              isAdmin={isAdmin}
              onPause={() => pause.mutate(vm.id)}
              onResume={() => resume.mutate(vm.id)}
              onRemove={() => handleRemove(vm)}
              onTest={() => handleTest(vm)}
              onEdit={() => setEditing(vm)}
            />
          ))}
        </div>
      )}

      <VmFormDialog open={showCreate} onClose={() => setShowCreate(false)} />
      <VmFormDialog
        open={!!editing}
        onClose={() => setEditing(undefined)}
        vm={editing}
      />
    </div>
  );
}

interface VmCardProps {
  vm: Vm;
  isAdmin: boolean;
  onPause: () => void;
  onResume: () => void;
  onRemove: () => void;
  onTest: () => void;
  onEdit: () => void;
}

function VmCard({ vm, isAdmin, onPause, onResume, onRemove, onTest, onEdit }: VmCardProps) {
  return (
    <Card className="p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div>
          <Link to={`/vms/${vm.id}`} className="font-heading font-bold text-brand-dark text-base hover:underline">
            {vm.slug}
          </Link>
          <p className="text-xs text-gray-400 mt-0.5">{maskSecret(vm.base_url, 24)}</p>
        </div>
        <StatusBadge status={vm.status} />
      </div>

      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-gray-400">Région</div>
          <div className="font-medium text-brand-dark">{vm.region ?? '—'}</div>
        </div>
        <div>
          <div className="text-gray-400">Poids</div>
          <div className="font-medium text-brand-dark">{vm.weight}</div>
        </div>
        <div>
          <div className="text-gray-400">Charge</div>
          <div className="font-medium text-brand-dark">
            {vm.active_jobs}/{vm.max_concurrent}
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-400">Vu {formatRelative(vm.last_seen_at)}</div>

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-brand-border">
          {vm.status === 'paused' ? (
            <Button size="sm" variant="secondary" onClick={onResume}>
              <Play size={12} /> Resume
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={onPause}>
              <Pause size={12} /> Pause
            </Button>
          )}
          <button
            type="button"
            onClick={onTest}
            className="btn-ghost text-xs"
            title="Tester la connectivité"
          >
            <Wifi size={12} /> Tester
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="btn-ghost text-xs"
            title="Modifier"
          >
            <Edit3 size={12} /> Modifier
          </button>
          <Link to={`/vms/${vm.id}`} className="btn-ghost text-xs">
            <RefreshCw size={12} /> Détail
          </Link>
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
            title="Supprimer la VM"
          >
            <Trash2 size={12} /> Supprimer
          </button>
        </div>
      )}
    </Card>
  );
}
